import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { NEWS_BODY_MAX, NEWS_TITLE_MAX, type NewsPostDto } from '@crm/shared'
import { deleteNewsPost, updateNewsPost } from '../../shared/api/news.api'
import { confirm } from '../../shared/ui/confirmStore'
import { useAuthStore } from '../auth/authStore'
import { authorName, canEditNews, formatNewsDate } from './utils'

interface Props {
  post: NewsPostDto
  highlighted?: boolean
}

export function NewsPostCard({ post, highlighted = false }: Props) {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const canEdit = canEditNews(user?.role, post.authorId, user?.id)

  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(post.title)
  const [body, setBody] = useState(post.body)

  const updateMut = useMutation({
    mutationFn: () => updateNewsPost(post.id, { title: title.trim(), body: body.trim() }),
    onSuccess: () => {
      setEditing(false)
      void qc.invalidateQueries({ queryKey: ['news'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: () => deleteNewsPost(post.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['news'] })
    },
  })

  function startEdit() {
    setTitle(post.title)
    setBody(post.body)
    setEditing(true)
  }

  function cancelEdit() {
    setEditing(false)
    setTitle(post.title)
    setBody(post.body)
  }

  async function confirmDelete() {
    if (
      !(await confirm({
        title: 'Удалить эту новость?',
        message: 'Действие необратимо.',
        danger: true,
        confirmText: 'Удалить',
      }))
    )
      return
    deleteMut.mutate()
  }

  const titleOk = title.trim().length > 0 && title.trim().length <= NEWS_TITLE_MAX
  const bodyOk = body.trim().length > 0 && body.trim().length <= NEWS_BODY_MAX

  return (
    <article
      id={`news-${post.id}`}
      className={`rounded-xl border bg-white p-4 transition-colors ${
        highlighted ? 'border-brand-300 ring-2 ring-brand-100' : 'border-gray-200'
      }`}
    >
      {!editing ? (
        <>
          <header className="flex items-start justify-between gap-3 mb-2">
            <h3 className="text-base font-semibold text-gray-900 leading-snug">{post.title}</h3>
            {canEdit && (
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={startEdit}
                  className="text-xs px-2 py-1 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                  title="Редактировать"
                >
                  Изм.
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMut.isPending}
                  className="text-xs px-2 py-1 rounded-md text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                  title="Удалить"
                >
                  Удалить
                </button>
              </div>
            )}
          </header>
          <p className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
            {post.body}
          </p>
          <footer className="mt-3 flex items-center justify-between text-xs text-gray-400">
            <span>{authorName(post.author)}</span>
            <time>{formatNewsDate(post.createdAt)}</time>
          </footer>
        </>
      ) : (
        <div className="space-y-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={NEWS_TITLE_MAX}
            className="w-full text-sm rounded-md border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-200"
            placeholder="Заголовок"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={NEWS_BODY_MAX}
            rows={5}
            className="w-full text-sm rounded-md border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-200 resize-y"
            placeholder="Текст новости"
          />
          {updateMut.isError && (
            <p className="text-xs text-red-600">Не удалось сохранить, попробуйте ещё раз.</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={cancelEdit}
              disabled={updateMut.isPending}
              className="text-xs px-3 py-1.5 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Отмена
            </button>
            <button
              onClick={() => updateMut.mutate()}
              disabled={updateMut.isPending || !titleOk || !bodyOk}
              className="text-xs px-3 py-1.5 rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {updateMut.isPending ? 'Сохраняем…' : 'Сохранить'}
            </button>
          </div>
        </div>
      )}
    </article>
  )
}
